import type { AccountSummary } from "@trader/shared/Money"
import { useState } from "react"
import { toast } from "../../ui/toasts.ts"
import { summaryDialog } from "./sessionSummary.ts"
import { messageFor, tagOf } from "./tradingApi.ts"
import { accountsStore } from "./tradingData.ts"

export interface EndSession {
  /** The confirm dialog is open. */
  readonly confirming: boolean
  readonly busy: boolean
  readonly error: string | null
  readonly ask: () => void
  readonly cancel: () => void
  readonly confirm: () => void
}

/**
 * Ending an account's session (docs/design/0008), from the Trading panel or the
 * replay strip: confirm, end it (the account stays selected, read-only), then show
 * its summary in the app's one summary dialog.
 */
export const useEndSession = (account: AccountSummary | null): EndSession => {
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirm = () => {
    if (account === null) return
    const id = account.id
    setBusy(true)
    setError(null)
    accountsStore.end(id).then(
      (ended) => {
        setBusy(false)
        setConfirming(false)
        summaryDialog.open(ended.id)
        toast("ok", "Session ended", `${ended.name} is read-only now. Its summary stays under Ended sessions.`)
      },
      (failure: unknown) => {
        setBusy(false)
        // Already over (ended in another tab, or reset): just show how it went.
        if (tagOf(failure) === "AccountArchived") {
          setConfirming(false)
          accountsStore.reload().catch(() => undefined)
          summaryDialog.open(id)
          return
        }
        const message = messageFor(failure)
        setError(`${message.title}: ${message.detail}`)
      }
    )
  }

  return {
    confirming,
    busy,
    error,
    ask: () => {
      setError(null)
      setConfirming(true)
    },
    cancel: () => {
      setConfirming(false)
      setError(null)
    },
    confirm
  }
}
